import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PERMISSIONS,
  PRODUCT_STATUSES,
  SIZE_CLASSES,
  SUPPLY_TYPES,
  type CategoryTreeResponse,
  type CreateProductDto,
  type Locale,
  type ProductMediaResponse,
  type ProductResponse,
  type ProductStatus,
  type SizeClass,
  type SupplyType,
  type UpdateProductDto,
  toSlug,
} from '@aps/contracts';
import { Button, ImageDropzone, Skeleton, formatSumInput, parseSumInput, useToast } from '@aps/ui';
import { ImageCropDialog } from './ImageCropDialog.js';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { can } from '../lib/session.js';
import { QueueError } from './WorkQueueScreen.js';
import { StockPanel } from './StockPanel.js';
import { PRODUCT_STATUS_LABELS } from './ProductsScreen.js';

const SUPPLY_LABELS: Record<SupplyType, Record<Locale, string>> = {
  IN_STOCK: { UZ: "Do'konda bor", RU: 'В наличии' },
  PRE_ORDER: { UZ: 'Buyurtma ostida', RU: 'Под заказ' },
};

const SIZE_LABELS: Record<SizeClass, Record<Locale, string>> = {
  NORMAL: { UZ: "O'rtacha", RU: 'Обычный' },
  BULKY: { UZ: 'Yirik', RU: 'Крупногабаритный' },
};

/** Forma holati — barcha maydonlar STRING, konvertatsiya yuborishda. */
interface FormState {
  sku: string;
  slug: string;
  nameUz: string;
  nameRu: string;
  brandId: string;
  categoryId: string;
  price: string;
  oldPrice: string;
  costPrice: string;
  /** "Tez kunda": narx ko'rsatilmaydi, savatga tushmaydi. */
  comingSoon: boolean;
  supplyType: SupplyType;
  leadTimeDays: string;
  sizeClass: SizeClass;
  status: ProductStatus;
  searchTags: string;
  oemCodes: string;
  fitsUz: string;
  fitsRu: string;
  descUz: string;
  descRu: string;
}

const EMPTY: FormState = {
  sku: '',
  slug: '',
  nameUz: '',
  nameRu: '',
  brandId: '',
  categoryId: '',
  price: '',
  oldPrice: '',
  costPrice: '',
  comingSoon: false,
  supplyType: 'IN_STOCK',
  leadTimeDays: '0',
  sizeClass: 'NORMAL',
  // Yangi tovar QORALAMA bo'lib tug'iladi: rasm va tavsifsiz tovar
  // vitrinaga chiqib ketmasin.
  status: 'DRAFT',
  searchTags: '',
  oemCodes: '',
  fitsUz: '',
  fitsRu: '',
  descUz: '',
  descRu: '',
};

function fromProduct(product: ProductResponse): FormState {
  return {
    sku: product.sku,
    slug: product.slug,
    nameUz: product.nameUz,
    nameRu: product.nameRu ?? '',
    brandId: '',
    categoryId: '',
    price: formatSumInput(product.price),
    oldPrice: product.oldPrice === null ? '' : formatSumInput(product.oldPrice),
    costPrice: product.costPrice === undefined ? '' : formatSumInput(product.costPrice),
    comingSoon: product.comingSoon,
    supplyType: product.supplyType,
    leadTimeDays: String(product.leadTimeDays),
    sizeClass: product.sizeClass,
    status: product.status,
    searchTags: product.searchTags ?? '',
    oemCodes: product.oemCodes ?? '',
    fitsUz: product.fitsUz ?? '',
    fitsRu: product.fitsRu ?? '',
    descUz: product.descUz ?? '',
    descRu: product.descRu ?? '',
  };
}

interface ProductFormScreenProps {
  /** `null` — yangi tovar. */
  productId: string | null;
  onBack: () => void;
  /**
   * Yaratilgandan keyin O'SHA tovarni ochish uchun.
   *
   * Ro'yxatga qaytarish noto'g'ri bo'lardi: rasm faqat mavjud tovarga
   * yuklanadi (`POST products/:id/media`), demak yangi tovar rasmsiz
   * qolib ketardi va odam uni qaytadan qidirishi kerak bo'lardi.
   */
  onCreated: (productId: string) => void;
}

/**
 * Tovar formasi — yaratish va tahrirlash.
 *
 * Tahrirlashda FAQAT o'zgargan maydonlar yuboriladi. Bu qulaylik emas,
 * majburiyat: server `price` maydoni KELGANIGA qarab `product.edit_price`
 * ruxsatini talab qiladi (`product.service.ts`). Hammasini yuboradigan
 * forma kontent menejerni o'z ishidan mahrum qilardi — u nomni
 * tuzatmoqchi bo'lganida 403 olardi.
 */
export function ProductFormScreen({
  productId,
  onBack,
  onCreated,
}: ProductFormScreenProps): ReactNode {
  const { locale, me } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const toast = useToast();

  const isNew = productId === null;
  const canSeeCost = can(me, PERMISSIONS.REPORT_FINANCE);
  const canEditPrice = can(me, PERMISSIONS.PRODUCT_EDIT_PRICE);

  const product = useQuery({
    queryKey: ['admin-product', productId],
    queryFn: () => api.adminProduct(productId ?? ''),
    enabled: !isNew,
  });

  const categories = useQuery({ queryKey: ['categories'], queryFn: () => api.categoryTree() });
  const brands = useQuery({ queryKey: ['brands'], queryFn: () => api.brands() });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [loaded, setLoaded] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Slug nomdan avtomatik quriladi, LEKIN odam unga qo'l tekkizgach
   * to'xtaydi. Bu bayroq komponent ichida: modul darajasidagi
   * o'zgaruvchi bo'lsa, ikkinchi tovarni ochganda oldingisining holati
   * qolib ketardi.
   */
  const [slugTouched, setSlugTouched] = useState(false);

  /**
   * `categorySlug` javobda bor, `categoryId` yo'q — shuning uchun
   * kategoriya slug orqali topiladi. Brend ham shunday: nomi bo'yicha.
   */
  const flatCategories = useMemo(() => flatten(categories.data ?? []), [categories.data]);

  useEffect(() => {
    const data = product.data;
    if (data === undefined) return;

    const next = fromProduct(data);
    next.categoryId = flatCategories.find((row) => row.slug === data.categorySlug)?.id ?? '';
    next.brandId = (brands.data ?? []).find((row) => row.name === data.brandName)?.id ?? '';

    setForm(next);
    setLoaded(next);
  }, [product.data, flatCategories, brands.data]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((current) => ({ ...current, [key]: value }));
    setError(null);
  };

  const priceTiyin = parseSumInput(form.price);
  const oldPriceTiyin = form.oldPrice.trim() === '' ? null : parseSumInput(form.oldPrice);
  const costTiyin = form.costPrice.trim() === '' ? null : parseSumInput(form.costPrice);

  /** Yuborishdan oldin klientda tekshiriladigan narsalar. */
  const problems: string[] = [];
  if (form.nameUz.trim() === '') problems.push(t.productNameRequired);
  if (isNew && form.sku.trim() === '') problems.push(t.productSkuRequired);
  if (isNew && form.slug.trim() === '') problems.push(t.productSlugRequired);
  if (form.brandId === '' || form.categoryId === '') problems.push(t.productRefRequired);
  // "Tez kunda" tovarda narx majburiy emas.
  if (!form.comingSoon && (priceTiyin === null || priceTiyin === '0')) {
    problems.push(t.productPriceRequired);
  }
  if (form.oldPrice.trim() !== '' && oldPriceTiyin === null)
    problems.push(t.productOldPriceInvalid);
  if (
    oldPriceTiyin !== null &&
    priceTiyin !== null &&
    BigInt(oldPriceTiyin) <= BigInt(priceTiyin)
  ) {
    // Server ham buni rad etadi; bu yerda — 400 ni kutmasdan aytish uchun.
    problems.push(t.productOldPriceTooLow);
  }
  if (form.supplyType === 'PRE_ORDER' && Number(form.leadTimeDays) <= 0) {
    problems.push(t.productLeadTimeRequired);
  }

  const save = useMutation({
    mutationFn: async (): Promise<ProductResponse> => {
      if (priceTiyin === null && !form.comingSoon) throw new Error('price');

      if (isNew) {
        const dto: CreateProductDto = {
          sku: form.sku.trim(),
          slug: form.slug.trim(),
          brandId: form.brandId,
          categoryId: form.categoryId,
          nameUz: form.nameUz.trim(),
          comingSoon: form.comingSoon,
          ...(priceTiyin === null ? {} : { price: priceTiyin }),
          supplyType: form.supplyType,
          leadTimeDays: Number(form.leadTimeDays),
          sizeClass: form.sizeClass,
          status: form.status,
          ...optional('nameRu', form.nameRu),
          ...optional('descUz', form.descUz),
          ...optional('descRu', form.descRu),
          ...optional('fitsUz', form.fitsUz),
          ...optional('fitsRu', form.fitsRu),
          ...optional('oemCodes', form.oemCodes),
          ...optional('searchTags', form.searchTags),
          ...(oldPriceTiyin === null ? {} : { oldPrice: oldPriceTiyin }),
          ...(costTiyin === null ? {} : { costPrice: costTiyin }),
        };
        return api.createProduct(dto);
      }

      const dto = buildPatch(form, loaded, {
        priceTiyin,
        oldPriceTiyin,
        costTiyin,
        canEditPrice,
      });
      return api.updateProduct(productId, dto);
    },
    onSuccess: (result) => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['admin-products'] });
      void client.invalidateQueries({ queryKey: ['admin-product', result.id] });
      /*
       * Natija ANIQ aytiladi. Ilgari forma ochiq qolib, yonida kichik
       * "Saqlandi" yozuvi turardi va operator o'zgarish bo'ldimi-yo'qmi
       * bilmasdi.
       *
       * Tahrirlashdan keyin ro'yxatga QAYTILADI (arxivlashdagidek).
       * Yaratishdan keyin esa o'sha tovar ochiladi: rasm faqat mavjud
       * tovarga yuklanadi, shuning uchun toast shuni aytadi.
       */
      if (isNew) {
        toast.show(t.toastProductCreated, 'success');
        onCreated(result.id);
        return;
      }
      toast.show(t.toastProductSaved, 'success');
      onBack();
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  const archive = useMutation({
    mutationFn: () => {
      // Tugma faqat tahrirlash rejimida chiziladi; `?? ''` yozilsa
      // xato holatda so'rov noto'g'ri manzilga jimgina ketardi.
      if (productId === null) throw new Error('archive: productId yo‘q');
      return api.archiveProduct(productId);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['admin-products'] });
      /*
       * Arxivlashdan keyin ekran YOPILADI va operator ro'yxatga
       * tushadi. Tovar u yerdan ham yo'qolgani uchun "bajarildimi?"
       * degan savolga javob qolmasdi.
       *
       * Saqlashda toast YO'Q: u yerda "Saqlandi" yozuvi forma
       * yonida turadi va ikki joyda takrorlanishi shovqin bo'lardi.
       */
      toast.show(t.toastProductArchived, 'success');
      onBack();
    },
    onError: (cause: unknown) => {
      setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
    },
  });

  const header = (
    <header className="adm-page__header">
      <h1 className="adm-page__title">{isNew ? t.productNew : t.productEdit}</h1>
      <Button variant="ghost" size="sm" onClick={onBack}>
        {t.back}
      </Button>
    </header>
  );

  if (!isNew && product.isPending) {
    return (
      <section className="adm-page">
        {header}
        <Skeleton height="320px" label={t.loading} />
      </section>
    );
  }

  if (!isNew && product.isError) {
    return (
      <section className="adm-page">
        {header}
        <QueueError
          message={product.error instanceof ApiError ? product.error.text(locale) : t.errorTitle}
          retryLabel={t.retry}
          onRetry={() => void product.refetch()}
        />
      </section>
    );
  }

  return (
    <section className="adm-page">
      {header}

      <div className="adm-columns">
        <div className="adm-stack">
          <Panel title={t.productMain}>
            {isNew ? (
              <>
                <Field
                  label={t.productSku}
                  value={form.sku}
                  onChange={(value) => {
                    set('sku', value);
                    // Slug odam tegmaguncha nomdan va artikuldan quriladi.
                    if (!slugTouched) set('slug', toSlug(`${form.nameUz} ${value}`) ?? '');
                  }}
                  hint={t.productSkuHint}
                />
                <Field
                  label={t.productSlug}
                  value={form.slug}
                  onChange={(value) => {
                    setSlugTouched(true);
                    set('slug', value);
                  }}
                  hint={t.productSlugHint}
                />
              </>
            ) : (
              <>
                <ReadOnly label={t.productSku} value={form.sku} hint={t.productSkuLocked} />
                <ReadOnly label={t.productSlug} value={form.slug} />
              </>
            )}

            <Field
              label={t.productNameUz}
              value={form.nameUz}
              onChange={(value) => {
                set('nameUz', value);
                if (isNew && !slugTouched) set('slug', toSlug(`${value} ${form.sku}`) ?? '');
              }}
            />
            <Field
              label={t.productNameRu}
              value={form.nameRu}
              onChange={(value) => {
                set('nameRu', value);
              }}
              hint={t.productNameRuHint}
            />

            <Select
              label={t.productBrand}
              value={form.brandId}
              options={(brands.data ?? []).map((row) => ({ value: row.id, label: row.name }))}
              placeholder={t.productPick}
              onChange={(value) => {
                set('brandId', value);
              }}
            />
            <Select
              label={t.productCategory}
              value={form.categoryId}
              options={flatCategories.map((row) => ({
                value: row.id,
                label: `${'— '.repeat(row.depth)}${row.nameUz}`,
              }))}
              placeholder={t.productPick}
              onChange={(value) => {
                set('categoryId', value);
              }}
            />
          </Panel>

          <Panel title={t.productPricing}>
            <label className="aps-field adm-check">
              <input
                type="checkbox"
                checked={form.comingSoon}
                disabled={!canEditPrice}
                onChange={(event) => {
                  set('comingSoon', event.target.checked);
                }}
              />
              <span className="aps-field__label">{t.productComingSoon}</span>
              <span className="aps-field__hint">{t.productComingSoonHint}</span>
            </label>
            <Field
              label={t.productPrice}
              value={form.price}
              disabled={!canEditPrice}
              onChange={(value) => {
                set('price', value);
              }}
              hint={t.productPriceHint}
            />
            <Field
              label={t.productOldPrice}
              value={form.oldPrice}
              disabled={!canEditPrice}
              onChange={(value) => {
                set('oldPrice', value);
              }}
              hint={t.productOldPriceHint}
            />
            {/*
              Tannarx — foyda ma'lumoti. `report.finance` ruxsati
              bo'lmaganda server uni javobga UMUMAN qo'shmaydi, shuning
              uchun maydon ham ko'rsatilmaydi: bo'sh maydon "tannarx
              yo'q" degan yolg'on ma'no berardi.
            */}
            {canSeeCost && (
              <Field
                label={t.productCost}
                value={form.costPrice}
                disabled={!canEditPrice}
                onChange={(value) => {
                  set('costPrice', value);
                }}
              />
            )}
          </Panel>
        </div>

        <div className="adm-stack">
          <Panel title={t.productSupply}>
            <Select
              label={t.productSupplyType}
              value={form.supplyType}
              options={SUPPLY_TYPES.map((value) => ({
                value,
                label: SUPPLY_LABELS[value][locale],
              }))}
              onChange={(value) => {
                set('supplyType', value as SupplyType);
              }}
            />
            <Field
              label={t.productLeadTimeField}
              value={form.leadTimeDays}
              onChange={(value) => {
                set('leadTimeDays', value.replace(/\D/gu, ''));
              }}
              hint={t.productLeadTimeHint}
            />
            <Select
              label={t.productSizeClass}
              value={form.sizeClass}
              options={SIZE_CLASSES.map((value) => ({ value, label: SIZE_LABELS[value][locale] }))}
              onChange={(value) => {
                set('sizeClass', value as SizeClass);
              }}
            />
            <Select
              label={t.colStatus}
              value={form.status}
              options={PRODUCT_STATUSES.map((value) => ({
                value,
                label: PRODUCT_STATUS_LABELS[value][locale],
              }))}
              onChange={(value) => {
                set('status', value as ProductStatus);
              }}
            />
          </Panel>

          <Panel title={t.productFindability}>
            <TextArea
              label={t.productSearchTags}
              value={form.searchTags}
              rows={3}
              onChange={(value) => {
                set('searchTags', value);
              }}
              hint={t.productSearchTagsHint}
            />
            <TextArea
              label={t.productOemCodes}
              value={form.oemCodes}
              rows={2}
              onChange={(value) => {
                set('oemCodes', value);
              }}
              hint={t.productOemHint}
            />
          </Panel>

          {!isNew && product.data !== undefined && (
            <Panel title={t.stockTitle}>
              <StockPanel product={product.data} supplyType={form.supplyType} />
            </Panel>
          )}

          {!isNew && can(me, PERMISSIONS.PRODUCT_EDIT_MEDIA) && (
            <Panel title={t.productMedia}>
              <MediaPanel productId={productId} />
            </Panel>
          )}

          <Panel title={t.productDescription}>
            <TextArea
              label={t.productFitsUz}
              value={form.fitsUz}
              rows={2}
              onChange={(value) => {
                set('fitsUz', value);
              }}
              hint={t.productFitsHint}
            />
            <TextArea
              label={t.productFitsRu}
              value={form.fitsRu}
              rows={2}
              onChange={(value) => {
                set('fitsRu', value);
              }}
            />
            <TextArea
              label={t.productDescUz}
              value={form.descUz}
              rows={4}
              onChange={(value) => {
                set('descUz', value);
              }}
            />
            <TextArea
              label={t.productDescRu}
              value={form.descRu}
              rows={4}
              onChange={(value) => {
                set('descRu', value);
              }}
            />
          </Panel>
        </div>
      </div>

      {problems.length > 0 && (
        <ul className="aps-error" role="alert">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}

      {error !== null && (
        <p className="aps-error" role="alert">
          {error}
        </p>
      )}

      <div className="adm-form__actions">
        <Button
          disabled={problems.length > 0 || save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          {save.isPending ? t.statusApplying : t.productSave}
        </Button>

        {!isNew && can(me, PERMISSIONS.PRODUCT_ARCHIVE) && (
          <Button
            variant="ghost"
            disabled={archive.isPending}
            onClick={() => {
              archive.mutate();
            }}
          >
            {t.productArchive}
          </Button>
        )}
      </div>
    </section>
  );
}

/* ------------------------------ yordamchilar ------------------------------ */

/** Bo'sh matn yuborilmaydi — `undefined` va bo'sh string boshqa narsa. */
function optional<K extends string>(
  key: K,
  value: string,
): Record<K, string> | Record<string, never> {
  const trimmed = value.trim();
  return trimmed === '' ? {} : ({ [key]: trimmed } as Record<K, string>);
}

/**
 * FAQAT o'zgargan maydonlarni yig'adi.
 *
 * Narx maydonlari `canEditPrice` bo'lmasa umuman qo'shilmaydi: server
 * ularning KELGANIGA qarab ruxsat talab qiladi, qiymat o'zgarganiga
 * emas. O'zgarmagan narxni yuborish kontent menejerga 403 berardi.
 */
function buildPatch(
  form: FormState,
  loaded: FormState | null,
  money: {
    priceTiyin: string | null;
    oldPriceTiyin: string | null;
    costTiyin: string | null;
    canEditPrice: boolean;
  },
): UpdateProductDto {
  const before = loaded ?? EMPTY;
  const dto: UpdateProductDto = {};

  if (form.brandId !== before.brandId) dto.brandId = form.brandId;
  if (form.categoryId !== before.categoryId) dto.categoryId = form.categoryId;
  if (form.nameUz !== before.nameUz) dto.nameUz = form.nameUz.trim();
  if (form.nameRu !== before.nameRu) dto.nameRu = nullable(form.nameRu);
  if (form.descUz !== before.descUz) dto.descUz = nullable(form.descUz);
  if (form.descRu !== before.descRu) dto.descRu = nullable(form.descRu);
  if (form.fitsUz !== before.fitsUz) dto.fitsUz = nullable(form.fitsUz);
  if (form.fitsRu !== before.fitsRu) dto.fitsRu = nullable(form.fitsRu);
  if (form.oemCodes !== before.oemCodes) dto.oemCodes = nullable(form.oemCodes);
  if (form.searchTags !== before.searchTags) dto.searchTags = nullable(form.searchTags);
  if (form.supplyType !== before.supplyType) dto.supplyType = form.supplyType;
  if (form.leadTimeDays !== before.leadTimeDays) dto.leadTimeDays = Number(form.leadTimeDays);
  if (form.sizeClass !== before.sizeClass) dto.sizeClass = form.sizeClass;
  if (form.status !== before.status) dto.status = form.status;

  if (money.canEditPrice) {
    if (form.price !== before.price && money.priceTiyin !== null) dto.price = money.priceTiyin;
    if (form.comingSoon !== before.comingSoon) dto.comingSoon = form.comingSoon;
    if (form.oldPrice !== before.oldPrice) dto.oldPrice = money.oldPriceTiyin;
    if (form.costPrice !== before.costPrice && money.costTiyin !== null) {
      dto.costPrice = money.costTiyin;
    }
  }

  return dto;
}

function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

interface FlatCategory {
  id: string;
  slug: string;
  nameUz: string;
  depth: number;
}

/** Daraxtni tanlash ro'yxati uchun tekislaydi — chuqurlik saqlanadi. */
function flatten(nodes: CategoryTreeResponse[]): FlatCategory[] {
  const out: FlatCategory[] = [];
  const walk = (list: CategoryTreeResponse[]): void => {
    for (const node of list) {
      out.push({ id: node.id, slug: node.slug, nameUz: node.nameUz, depth: node.depth });
      walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

function Panel({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <section className="adm-panel">
      <h2 className="adm-panel__title">{title}</h2>
      <div className="adm-panel__body">{children}</div>
    </section>
  );
}

/**
 * Maydon.
 *
 * Yorliq `htmlFor` bilan bog'lanadi, izoh esa `aria-describedby` bilan —
 * ikkalasi ham `<label>` ichida bo'lsa, ekran o'quvchi maydon NOMI
 * sifatida butun izohni ham o'qib berardi.
 */
function Field({
  label,
  value,
  onChange,
  hint,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string | undefined;
  disabled?: boolean;
}): ReactNode {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className="aps-field">
      <label className="aps-field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="aps-field__input"
        value={value}
        disabled={disabled}
        {...(hint === undefined ? {} : { 'aria-describedby': hintId })}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      {hint !== undefined && (
        <span className="aps-field__hint" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  );
}

function ReadOnly({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): ReactNode {
  return (
    <div className="aps-field">
      <span className="aps-field__label">{label}</span>
      <span className="adm-kv__value">{value}</span>
      {hint !== undefined && <span className="aps-field__hint">{hint}</span>}
    </div>
  );
}

function Select({
  label,
  value,
  options,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  placeholder?: string;
}): ReactNode {
  const id = useId();

  return (
    <div className="aps-field">
      <label className="aps-field__label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="aps-field__input"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextArea({
  label,
  value,
  rows,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  rows: number;
  onChange: (value: string) => void;
  hint?: string;
}): ReactNode {
  const id = useId();
  const hintId = `${id}-hint`;

  return (
    <div className="aps-field">
      <label className="aps-field__label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className="aps-field__input adm-note__input"
        rows={rows}
        value={value}
        {...(hint === undefined ? {} : { 'aria-describedby': hintId })}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      {hint !== undefined && (
        <span className="aps-field__hint" id={hintId}>
          {hint}
        </span>
      )}
    </div>
  );
}

/* -------------------------------- rasmlar -------------------------------- */

/**
 * Tovar rasmlari.
 *
 * Yuklash NAVBAT orqali qayta ishlanadi (uch WebP o'lcham), shuning
 * uchun javob darhol tayyor URL bermaydi: `thumbUrl` biroz keyin
 * paydo bo'ladi. Ro'yxat shu sababdan yuklangandan keyin qayta
 * o'qiladi.
 */
function MediaPanel({ productId }: { productId: string }): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Kesish navbati: har fayl avval kesish oynasidan o'tadi, so'ng hammasi birga yuklanadi.
  const [pending, setPending] = useState<File[]>([]);
  const [ready, setReady] = useState<File[]>([]);
  const [total, setTotal] = useState(0);
  // Standart O'CHIQ: dizayn qilingan rasm (qora fon, yozuv) buzilmasin.
  const [removeBackground, setRemoveBackground] = useState(false);
  const toast = useToast();

  const media = useQuery({
    queryKey: ['product-media', productId],
    queryFn: () => api.productMedia(productId),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['product-media', productId] });
    // Ro'yxatdagi "rasm bor" ustuni ham yangilansin.
    void client.invalidateQueries({ queryKey: ['admin-products'] });
  };

  const onError = (cause: unknown): void => {
    setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
  };

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      // Ketma-ket: server har rasmni navbatga qo'yadi va bir vaqtda
      // o'nta yuborish faqat navbatni to'ldiradi.
      for (const file of files) await api.uploadProductMedia(productId, file, removeBackground);
    },
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError,
  });

  /**
   * Navbatdagi fayl hal bo'ldi: kesildi, kesmasdan olindi (`file`) yoki
   * tashlandi (`null`). Oxirgisi tugagach tayyorlar yuklanadi.
   */
  const settle = (file: File | null): void => {
    const rest = pending.slice(1);
    const done = file === null ? ready : [...ready, file];
    setPending(rest);
    if (rest.length > 0) {
      setReady(done);
      return;
    }
    setReady([]);
    setTotal(0);
    if (done.length > 0) upload.mutate(done);
  };

  const reprocess = useMutation({
    mutationFn: () => api.reprocessProductMedia(productId, removeBackground),
    onSuccess: (result) => {
      setError(null);
      toast.show(t.productMediaReprocessed(result.queued), 'success');
      refresh();
      // Variantlar navbatda tayyor bo'ladi: bir necha soniyadan keyin ro'yxatni yana o'qiymiz.
      window.setTimeout(refresh, 4000);
    },
    onError,
  });

  const setPrimary = useMutation({
    mutationFn: (mediaId: string) => api.setPrimaryMedia(mediaId),
    onSuccess: refresh,
    onError,
  });

  const remove = useMutation({
    mutationFn: (mediaId: string) => api.deleteProductMedia(mediaId),
    onSuccess: refresh,
    onError,
  });

  return (
    <>
      <label className="aps-field adm-check">
        <input
          type="checkbox"
          checked={removeBackground}
          disabled={upload.isPending}
          onChange={(event) => {
            setRemoveBackground(event.target.checked);
          }}
        />
        <span className="aps-field__label">{t.productMediaRemoveBg}</span>
        <span className="aps-field__hint">{t.productMediaRemoveBgHint}</span>
      </label>
      <ImageDropzone
        label={t.productMediaUpload}
        hint={t.productMediaHint}
        disabled={upload.isPending}
        onFiles={(files) => {
          if (files.length === 0) return;
          setError(null);
          setReady([]);
          setTotal(files.length);
          setPending(files);
        }}
      />

      {pending[0] !== undefined && (
        <ImageCropDialog
          key={`${String(pending.length)}-${pending[0].name}`}
          file={pending[0]}
          position={{ index: total - pending.length + 1, total }}
          onDone={settle}
          onSkip={() => {
            settle(pending[0] ?? null);
          }}
          onCancel={() => {
            settle(null);
          }}
        />
      )}

      {error !== null && (
        <p className="aps-error" role="alert">
          {error}
        </p>
      )}

      {media.isPending && <Skeleton height="80px" label={t.loading} />}

      {media.data !== undefined && media.data.length === 0 && (
        <p className="aps-muted">{t.productMediaEmpty}</p>
      )}

      {media.data !== undefined && media.data.length > 0 && (
        <div className="adm-media__reprocess">
          <Button
            size="sm"
            variant="ghost"
            disabled={reprocess.isPending}
            onClick={() => {
              reprocess.mutate();
            }}
          >
            {t.productMediaReprocess}
          </Button>
          <span className="aps-field__hint">{t.productMediaReprocessHint}</span>
        </div>
      )}

      {media.data !== undefined && media.data.length > 0 && (
        <ul className="adm-media">
          {media.data.map((item) => (
            <MediaRow
              key={item.id}
              item={item}
              onPrimary={() => {
                setPrimary.mutate(item.id);
              }}
              onRemove={() => {
                remove.mutate(item.id);
              }}
            />
          ))}
        </ul>
      )}
    </>
  );
}

function MediaRow({
  item,
  onPrimary,
  onRemove,
}: {
  item: ProductMediaResponse;
  onPrimary: () => void;
  onRemove: () => void;
}): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);

  return (
    <li className="adm-media__row">
      {/*
        `thumbUrl` navbat rasmni qayta ishlagunicha `null` bo'ladi —
        shunda originalga tushamiz, bo'sh katak ko'rsatmaymiz.
      */}
      <img className="adm-media__thumb" src={item.thumbUrl ?? item.originalUrl} alt="" />
      <div className="adm-media__actions">
        {item.isPrimary ? (
          <span className="adm-badge adm-badge--active">{t.productMediaPrimary}</span>
        ) : (
          <Button size="sm" variant="ghost" onClick={onPrimary}>
            {t.productMediaMakePrimary}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onRemove}>
          {t.productMediaRemove}
        </Button>
      </div>
    </li>
  );
}
